import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";

import { BASE_URL } from "./constants";
import useWebRTCCall from "./useWebRTCCall";
import CallUI from "./CallUI";

// =====================================================
// CallProvider — APP-LEVEL call manager
// The incoming-call ring must fire no matter which screen the user is on, so
// the WebRTC hook and the full-screen CallUI live HERE (mounted once around the
// whole router) instead of inside the /chat route. Outgoing calls are still
// started from the chat header — Chat just reads `startCall`/`call` from this
// context. Messaging keeps its own socket untouched; this is a dedicated,
// always-on socket used purely for call signaling.
// =====================================================

// One persistent socket for call signaling, created outside the component so it
// survives navigation. Same connection options as the rest of the app.
const callSocket = io(BASE_URL, { transports: ["websocket", "polling"] });

const CallContext = createContext({
  startCall: () => {},
  call: { state: "idle" },
});

export const useCall = () => useContext(CallContext);

export default function CallProvider({ children }) {
  const location = useLocation();

  // There's no global auth store, so re-read the logged-in identity from
  // localStorage on every navigation. This picks up login/logout without
  // polling: after signing in the user always navigates, which re-runs this.
  const [auth, setAuth] = useState(() => ({
    userId: localStorage.getItem("userId"),
    username: localStorage.getItem("username"),
  }));

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    setAuth((prev) =>
      prev.userId === userId && prev.username === username
        ? prev
        : { userId, username }
    );
  }, [location.pathname]);

  const webrtc = useWebRTCCall({
    socket: callSocket,
    userId: auth.userId,
    username: auth.username,
  });

  // Register in the personal room so the server can deliver `incomingCall` to
  // THIS socket — and re-join on reconnect so a dropped socket keeps ringing.
  useEffect(() => {
    if (!auth.userId) return;
    const join = () => callSocket.emit("join", auth.userId);
    join();
    callSocket.on("connect", join);
    return () => callSocket.off("connect", join);
  }, [auth.userId]);

  const value = useMemo(
    () => ({ startCall: webrtc.startCall, call: webrtc.call }),
    [webrtc.startCall, webrtc.call]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      <CallUI
        call={webrtc.call}
        localStream={webrtc.localStream}
        remoteStream={webrtc.remoteStream}
        muted={webrtc.muted}
        cameraOff={webrtc.cameraOff}
        peerAvatar={webrtc.call.peerAvatar}
        onAccept={webrtc.acceptCall}
        onReject={webrtc.rejectCall}
        onEnd={webrtc.endCall}
        onToggleMute={webrtc.toggleMute}
        onToggleCamera={webrtc.toggleCamera}
      />
    </CallContext.Provider>
  );
}
